import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
  Outlet,
} from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'
import { queryClient } from '~/lib/queryClient'
import { currentUserQuery } from '~/lib/auth'
import type { Stage } from '~/lib/types'
import AppShell from '~/components/AppShell'
import NotFound from '~/pages/NotFound'
import Home from '~/pages/Home'
import Login from '~/pages/Login'
import Dashboard from '~/pages/Dashboard'
import Conversations from '~/pages/Conversations'
import ConversationDetail from '~/pages/ConversationDetail'
import Courses from '~/pages/Courses'
import Students from '~/pages/Students'
import Team from '~/pages/Team'

const rootRoute = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: Outlet,
  notFoundComponent: NotFound,
})

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: Home,
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  // Google failures redirect back here with ?error=…; ?redirect= remembers
  // where the guard bounced the visitor from.
  validateSearch: (
    search: Record<string, unknown>,
  ): { error?: string; redirect?: string } => ({
    error: typeof search.error === 'string' ? search.error : undefined,
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
  component: Login,
})

/*
  Pathless layout: everything signed-in renders inside AppShell, but each
  section keeps its own top-level URL (/dashboard, /conversations, /courses)
  rather than being nested under one prefix.
*/
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ context, location }) => {
    const user = await context.queryClient.ensureQueryData(currentUserQuery)
    if (!user) throw redirect({ to: '/login', search: { redirect: location.href } })
    return { user }
  },
  component: AppShell,
})

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/dashboard',
  component: Dashboard,
})

const STAGES: (Stage | 'all')[] = [
  'all',
  'not_initiated',
  'initiated',
  'engaged',
  'interested',
  'converted',
  'lost',
]

// Filters live in the URL so a filtered view is shareable and survives reload.
// The detail route validates the same params, so returning to the list keeps
// whatever filter you arrived with.
const conversationSearch = (
  search: Record<string, unknown>,
): { stage: Stage | 'all'; escalated: boolean } => ({
  stage: STAGES.includes(search.stage as Stage | 'all') ? (search.stage as Stage | 'all') : 'all',
  escalated: search.escalated === true || search.escalated === 'true',
})

const conversationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/conversations',
  validateSearch: conversationSearch,
  component: Conversations,
})

const conversationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/conversations/$conversationId',
  validateSearch: conversationSearch,
  component: ConversationDetail,
})

const coursesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/courses',
  component: Courses,
})

const studentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/students',
  component: Students,
})

const teamRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/team',
  component: Team,
})

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  appRoute.addChildren([
    dashboardRoute,
    conversationsRoute,
    conversationRoute,
    coursesRoute,
    studentsRoute,
    teamRoute,
  ]),
])

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
